import { RECOMMENDATION_TYPES } from "../common/constants";

// Builds the POST /qa_qc/check_draft body from the recommendation form's state.

// The condition must travel as its NAME, never its id — the detector reads the words, and the leading
// number is a client-specific code ("1 - System failure" and "1 - New or nearly new" both exist). Sending
// an id does NOT error: the rule simply returns clean, which is this endpoint's one silent failure.
const conditionName = (options, id) => (id && options.find(o => o.id === id)?.name) || undefined;

const isEmpty = v => v === "" || v === null || v === undefined;

// A recommendation carries ONE installed year and ONE condition, not two: ASSET reads them from
// assetFormData, SYSTEM from project. Keys that do not apply to the type are omitted, not nulled.
export const buildDraftPayload = ({ project, assetFormData, conditionOptions = [], projectId, recommendationId }) => {
    const isAsset = project.recommendation_type === RECOMMENDATION_TYPES.ASSET;
    const options = conditionOptions || [];

    const payload = {
        project_id: projectId,
        // The form holds this as `selectedProject` (the route param); `project` has no id field at all.
        recommendation_id: recommendationId || null,
        // Load-bearing for the condition rule: without a subject it cannot tell the unit from its
        // neighbours, which a narrative routinely also describes.
        description: project.description,
        note_html: project.note_html,
        // Optional, but lets the model read an unfamiliar client scale.
        condition_options: options.map(o => o.name).filter(Boolean),
        // null = run every rule; they run concurrently backend-side, so this stays one call.
        rule_keys: null
    };

    if (isAsset) {
        if (!isEmpty(assetFormData.installed_year)) payload.asset_installed_year = assetFormData.installed_year;
        if (!isEmpty(assetFormData.installed_year_status))
            payload.asset_installed_year_status = assetFormData.installed_year_status;
        const name = conditionName(options, assetFormData.client_asset_condition_id);
        if (name) payload.asset_condition = name;
    } else {
        if (!isEmpty(project.installed_year)) payload.installed_year = project.installed_year;
        const name = conditionName(options, project.asset_condition_id);
        if (name) payload.condition = name;
    }

    return payload;
};

// TYPE-AWARE on purpose: "both years and both conditions are empty" would be true of every SYSTEM
// recommendation, so that test would skip every call and the feature would check nothing.
export const isWorthChecking = payload => {
    if (!payload.note_html || !payload.note_html.trim()) return false;
    return (
        !isEmpty(payload.installed_year) ||
        !isEmpty(payload.asset_installed_year) ||
        !isEmpty(payload.condition) ||
        !isEmpty(payload.asset_condition)
    );
};
